import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import type {
  CreateOrderResultUI,
  CreatedOrderUI,
} from "../../../model/order.model";
import {
  formatDate,
  formatItemTotal,
  formatQty,
  getStatusColor,
  normalizeStatusLabel,
} from "./utilsOrderResultDialog";

import { HeaderDialog } from "./HeaderDialog";

type FailedOrderUI = CreatedOrderUI & { errorMessage: string };

type Props = {
  open: boolean;
  result: CreateOrderResultUI | null;
  header: { title: string; subtitle: string };
  createdList: CreatedOrderUI[];
  failedList: FailedOrderUI[];
  onClose: () => void;
  onNewOrder: () => void;
  onConsult: (orderCode: string) => void;
  resolveProductName?: (productId: string) => string | undefined;
  resolveBranchName?: () => string | undefined;
};

interface ITABLE_COLUMNS {
  id: string;
  label: string;
  width?: number | string;
  maxWidth?: number;
  align?: "left" | "right" | "inherit" | "center" | "justify" | undefined;
}

const TABLE_COLUMNS: ITABLE_COLUMNS[] = [
  { id: "order", label: "Ordem", width: 100 },
  { id: "branch", label: "Filial", width: 210, align: "center" },
  { id: "date", label: "Data de entrega", width: 120, align: "center" },
  { id: "product", label: "Produto", width: 160, align: "center" },
  { id: "qty", label: "Quantidade (L)", width: 115, align: "right" },
  { id: "price", label: "Preço", width: 120, align: "center" },
  { id: "term", label: "Prazo", width: 100 },
  {
    id: "status",
    label: "Status",
    width: 110,
    maxWidth: 500,
  },
];

export function OrderResultDialogDesktop({
  open = true,
  result,
  createdList,
  failedList,
  onClose,
  onNewOrder,
  onConsult,
  resolveProductName,
  resolveBranchName,
}: Props) {
  const rows = useMemo(() => {
    const branch = resolveBranchName?.() ?? "Atem Distr Petroleo - Manaus";

    const success = createdList.map((o) => ({
      ...o,
      kind: "success",
      statusLabel: normalizeStatusLabel(o.status),
      statusColor: getStatusColor(o.status),
    }));

    const errors = failedList.map((o) => ({
      ...o,
      kind: "error",
      orderCode: o.orderCode || "Não gerada",
      statusLabel: `Erro: ${o.errorMessage}`,
      statusColor: "text.secondary",
    }));

    return [...success, ...errors].map((r) => ({
      kind: r.kind,
      statusColor: r.statusColor,
      statusLabel: r.statusLabel,
      data: [
        r.orderCode || "-",
        branch,
        formatDate(r.deliveryDateISO),
        resolveProductName?.(r.productId) ?? r.productId,
        formatQty(r.quantityLiters),
        formatItemTotal(r.price, r.quantityLiters),
        r.paymentLabel ?? "-",
        r.statusLabel,
      ],
    }));
  }, [createdList, failedList, resolveProductName, resolveBranchName]);

  const header = useMemo(() => {
    if (!result) return null;

    if (result.overall === "SUCESSO") {
      return {
        title: "Sucesso!",
        subtitle: "Seu pedido foi efetuado com sucesso!",
        severity: "success",
      };
    }

    if (result.overall === "PARCIAL") {
      return {
        title: "Sucesso parcial",
        subtitle: "Alguns itens foram criados, outros falharam.",
        severity: "warning",
      };
    }

    return {
      title: "Erro ao criar pedido",
      subtitle: "Não foi possível processar o pedido. Confira os detalhes:",
      severity: "error",
    };
  }, [result]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Detalhes do Pedido
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 1 }}>
        {header && (
          <HeaderDialog
            variant="desktop"
            title={header.title}
            subtitle={header.subtitle}
            severity={header.severity}
          />
        )}

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderRadius: 2,
            maxHeight: 450,
            overflowX: "auto",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{ tableLayout: "auto", width: "100%", minWidth: 900 }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: "grey.50",
                    fontWeight: 800,
                    px: 2,
                    py: 2,
                    borderBottom: "1px solid #eee",
                  },
                }}
              >
                {TABLE_COLUMNS.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col?.align || "left"}
                    style={{
                      ...(col.width ? { minWidth: col.width } : {}),
                      ...(col.maxWidth ? { maxWidth: col.maxWidth } : {}),
                    }}
                  >
                    {" "}
                    {col.label}{" "}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    "& td": {
                      px: 2,
                      py: 2.5,
                      borderBottom: "1px solid #f5f5f5",
                    },
                  }}
                >
                  {row.data.map((value, i) => (
                    <TableCell
                      key={i}
                      align={TABLE_COLUMNS[i].align || "left"}
                      sx={{
                        width: TABLE_COLUMNS[i].width,
                        maxWidth: TABLE_COLUMNS[i].maxWidth ?? "auto",

                        color: i === 0 ? "text.primary" : "text.secondary",
                        fontWeight: i === 0 ? 700 : 400,
                        ...(i === 7 && {
                          // Estilização especial para a coluna Status
                          minWidth: row.kind === "error" ? 225 : 100,
                          color: row.statusColor,
                          fontWeight: row.kind === "success" ? 700 : 400,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }),
                      }}
                    >
                      {value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          mt={3}
          display="flex"
          justifyContent="flex-end"
          gap={3}
          alignItems="center"
        >
          <Typography
            onClick={onNewOrder}
            sx={{
              color: "error.main",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: "0.875rem",
            }}
          >
            Fazer novo pedido
          </Typography>
          <Button
            variant="contained"
            color="error"
            disableElevation
            sx={{ borderRadius: 2, px: 4, py: 1, fontWeight: 700 }}
            disabled={createdList.length === 0}
            onClick={() => onConsult(createdList[0]?.orderCode)}
          >
            Consultar pedido
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
